import { Alert, Button, Form, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import CityApi from '../../../functions/api/partner/CityApi';
import UserApi from '../../../functions/api/user/UserApi';
import TypeApi from '../../../functions/api/vehicle/TypeApi';
import { City, VehicleType } from '../../../type/entity';
import {
  filterOption,
  normalizeOptions,
} from '../../booking/form/CustomSelect';

interface FormData {
  username: string;
  part_name: string;
  phone: string;
  city_id: number;
  vhc_type_id: number;
  password: string;
  re_password: string;
}

interface FilterOptions {
  cities: City[];
  vhcTypes: VehicleType[];
}

export default function Register() {
  const history = useHistory();
  const [error, setError] = useState<string>();
  const [isDone, setDone] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    cities: [],
    vhcTypes: [],
  });

  useEffect(() => {
    fetchSelectOptions().then(setFilterOptions);
  }, []);

  function getCityName(cityId: number) {
    const city = filterOptions.cities.find(({ city_id }) => city_id === cityId);

    return city?.city_name ?? '';
  }

  function getVhcTypeName(vhcTypeId: number) {
    const vehicleType = filterOptions.vhcTypes.find(
      ({ vhc_type_id }) => vhc_type_id === vhcTypeId
    );

    return vehicleType?.vhc_type_name ?? '';
  }

  async function handleSubmit(values: FormData) {
    const param = {
      username: values.username,
      part_name: values.part_name,
      phone: values.phone,
      city_id: values.city_id,
      city_name: getCityName(values.city_id),
      vhc_type_id: values.vhc_type_id,
      vhc_type_name: getVhcTypeName(values.vhc_type_id),
      password: values.password,
    };

    const user = await UserApi.registerByUser(param);

    if (user?.data) {
      setDone(true);
      return;
    }

    setError(user?.message ?? 'Đã xảy ra lỗi, vui lòng thử lại sau ít phút !');
  }

  return (
    <div className="container">
      <Form
        layout="vertical"
        onFinish={handleSubmit}
        className="col-12 col-md-4 mx-auto"
      >
        <h2>Đăng ký</h2>
        {error && <Alert type="error" message={error} className="my-3" />}
        <Form.Item
          name="username"
          label="Tên đăng nhập"
          rules={[{ required: true, message: 'Vui lòng nhập username' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="password"
          label="Mật khẩu"
          rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
        >
          <Input type="password" />
        </Form.Item>
        <Form.Item
          name="re_password"
          label="Nhập lại mật khẩu"
          dependencies={['password']}
          hasFeedback
          rules={[
            { required: true, message: 'Vui lòng nhập mật khẩu' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('Mật khẩu nhập lại không trùng khớp')
                );
              },
            }),
          ]}
        >
          <Input type="password" />
        </Form.Item>
        <Form.Item
          name="part_name"
          label="Tên partner"
          hasFeedback
          rules={[{ required: true, message: 'Vui lòng nhập tên partner' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Số điện thoại"
          rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="city_id"
          label="Thành phố"
          rules={[{ required: true, message: 'Vui lòng chọn thành phố' }]}
        >
          {filterOptions.cities && (
            <Select
              showSearch
              filterOption={filterOption}
              options={normalizeOptions(
                'city_name',
                'city_id',
                filterOptions.cities
              )}
            />
          )}
        </Form.Item>
        <Form.Item
          name="vhc_type_id"
          label="Loại phương tiện"
          rules={[
            { required: true, message: 'Vui lòng chọn loại phương tiện' },
          ]}
        >
          <Select>
            {filterOptions.vhcTypes.map((t: any) => (
              <Select.Option key={t.vhc_type_id} value={t.vhc_type_id}>
                {t.vhc_type_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {isDone && <SuccessMessage />}
        <Button htmlType="submit" block type="primary">
          Đăng ký
        </Button>
        <Link
          to="/login"
          component={(props) => (
            <Button block type="default" className="mt-2" {...props}>
              Hủy
            </Button>
          )}
        />
      </Form>
    </div>
  );
}

function SuccessMessage() {
  return (
    <Alert
      type="success"
      message={
        <>
          <p>Đăng ký thành công.</p>
          Quay lại trang <Link to="/login">Đăng nhập</Link>
        </>
      }
      className="my-3"
    />
  );
}

async function fetchSelectOptions(): Promise<FilterOptions> {
  const cities = await CityApi.getAll();
  const vhcTypes = await TypeApi.getAll();

  return { cities, vhcTypes };
}
